import React from 'react';
import type { ClassReportType, HighlightedProps } from '../../types';
import type { DateRange } from '../../../util/types';
import type { DurationUnit } from '../../../util/dates';
import * as d3 from 'd3';
import { useI18n } from '@whizz/react-i18n';
import { SectionLayout } from '../modules';
import { StudentProgress } from '../../../components';
import { YEAR_IN_WEEKS, convertWeeksToYears } from '../../../util/dates';
import { DateTime, Interval } from 'luxon';

type Props = {
  classReport: ClassReportType;
  showOthersData: boolean
  hideAverage?: boolean
  chartIsExpanded?: boolean
  dateRange?: DateRange
} & HighlightedProps;

const MathsAgeImprovements = ({
  classReport,
  showOthersData,
  highlightedLabel,
  highlightedIds,
  hideAverage,
  chartIsExpanded,
  dateRange,
  onElementClick,
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const getHeaderValue = (): number | null => {
    if (highlightedLabel) {
      const studentListWithZeroMathsAgeRemoved = classReport.students.all.filter((student) => highlightedIds.includes(student.id) && student.chartsData.mathsAge > 0);
      const mean = d3.mean(studentListWithZeroMathsAgeRemoved.map((student) => student.chartsData.mathsAgeImprovement));
      return mean != undefined ? mean : 0;
    }
    return null;
  };

  const calculateWeeksToYears = (value: number | null, useUnits?: DurationUnit): [ number | null, DurationUnit ] => {
    /**
    * If the value (number of weeks) is greater than 52 (1 year) convert value and units to 'year'
    */
    let unit: DurationUnit = 'unitWeek';
    if (value === null) {
      return [ value, 'unitWeek' ];
    }
    let convertToYears = value > YEAR_IN_WEEKS || (useUnits && useUnits === 'unitYear');
    if (useUnits && useUnits === 'unitWeek') {
      convertToYears = false;
    }
    if (convertToYears) {
      value = convertWeeksToYears(value);
      unit = 'unitYear';
    } else {
      // Less than 1 year, so format number (weeks) to 1 decimal places
      value = Number(value.toFixed(1));
      unit = 'unitWeek';
    }
    return [ value, unit ];
  };

  let allStudents = classReport.students.all;
  const highlightValue = getHeaderValue();
  const [ value, valueUnit ] = calculateWeeksToYears(Number(classReport.mathsAgeImprovementAverage));
  const [ highlightValueOrNull, highlightUnit ] = calculateWeeksToYears(highlightValue, showOthersData ? valueUnit : undefined);

  const convertToYears = highlightValueOrNull !== null && highlightUnit === 'unitYear' || highlightValueOrNull === null && valueUnit === 'unitYear';

  if (convertToYears) {
    // Convert the `mathsAgeImprovement` to years
    allStudents = allStudents.map((student) => {
      return ({
        ...student,
        chartsData: {
          ...student.chartsData,
          mathsAgeImprovement: Number(convertWeeksToYears(student.chartsData.mathsAgeImprovement).toFixed(2)),
        },
      });
    });
  }
  // Calculate the number of weeks in the date range
  const dateRangeInterval = (dateRange && DateTime.isDateTime(dateRange.startDate!)) ? Interval.fromDateTimes(dateRange.startDate!, dateRange.endDate ?? DateTime.now()) : null;
  const numberOfWeeks = dateRangeInterval ? Math.round(dateRangeInterval?.toDuration('weeks').weeks) : null;

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.title')}
      value={showOthersData ? value?.toFixed(2) : null}
      highlightLabel={highlightedLabel}
      highlightValue={highlightValueOrNull?.toFixed(2)}
      unit={translate(`pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.${highlightValueOrNull ? highlightUnit : valueUnit}`, { count: parseFloat(valueUnit) })}
      moreInfo={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.info')}
      className={'pdf-no-break'}
      data-cy="maths-age-improvement"
      hideAverage={hideAverage}
      chartIsExpanded={chartIsExpanded}
      id='maths-age-improvements-container'
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={allStudents ?? []}
        chartType={'mathsAgeImprovement'}
        highlightedIds={highlightedIds}
        removeUnassessed={true}
        excellentBreaking={numberOfWeeks}
        thinAxis={numberOfWeeks === null}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

MathsAgeImprovements.displayName = 'MathsAgeImprovements';

export default MathsAgeImprovements;
